
import { mapGetters, mapActions } from "vuex";

import ProductsFetch from "./ProductsFetch";
import { delay } from "~/utils";
import getShopifyProductByHandle from "~/utils/shopify-utils";

export default {
  components: { ProductsFetch },

  props: {
    slice: { type: Object, required: true },
    context: { type: Object, default: () => {} },
    /** Searchspring — Bundle PDP slice: tag is omitted in CMS; API uses bundle-pdp. */
    forceBundleTag: { type: Boolean, default: false },
  },

  data: () => ({ products: [] }),

  async fetch() {
    const { max_products_limit: limit, skip_params: skipParams } =
      this.slice.primary;

    const tag = this.effectiveTag;

    const payload = { tag, limit, sku: this.context.sku, skipParams };
    await this.$store.dispatch("recommendations/fetchProducts", payload);

    let recommendedProducts = this.getProducts(tag);

    // Bundle fix: SearchSpring recommendations only include ~20 variants.
    // For bundle-pdp, hydrate the FIRST product from Shopify (full variants + correct PDP title/metafields),
    // but keep SearchSpring ids/mappings for tracking.
    if (this.isBundleType && recommendedProducts?.length) {
      const firstRecProduct = recommendedProducts[0];
      const handle = this.$route.params?.handle;
      const variantId =
        this.$route.query?.variant ?? this.context?.selectedVariant?.id ?? null;

      if (handle) {
        const shopifyProduct = await getShopifyProductByHandle(
          handle,
          variantId,
          this.$getShopifyData
        );

        if (shopifyProduct) {
          recommendedProducts = [
            {
              ...firstRecProduct,
              ...shopifyProduct,
              // keep SearchSpring id for beacons + visible tracking lookups
              id: firstRecProduct?.id ?? shopifyProduct.id,
              recId:
                firstRecProduct?.recId ??
                firstRecProduct?.id ??
                shopifyProduct.id,
              mappings: firstRecProduct?.mappings,
            },
            ...recommendedProducts.slice(1),
          ];
        }
      }
    }

    this.products = recommendedProducts || [];

    try {
      await this.trackBeacon({ type: "render", tag });
    } catch (error) {
      this.$bugsnag.notify(error, e => {
        e.context = "ProductCatalogueV2 VariationSearchspring fetch";
      });
    }
  },

  fetchOnServer: false,

  computed: {
    ...mapGetters("bag", ["lines"]),
    ...mapGetters("recommendations", ["getProducts", "getVisibleProductIds"]),
    effectiveTag() {
      if (this.forceBundleTag) {
        return this.slice.primary?.tag ?? "bundle-pdp";
      }
      return this.slice.primary?.tag;
    },
    isBundleVariation() {
      return this.forceBundleTag || this.slice.primary?.tag === "bundle-pdp";
    },
    visibleProductIds() {
      return this.getVisibleProductIds(this.effectiveTag);
    },
    isBundleType() {
      return this.isBundleVariation;
    },
  },

  mounted() {
    const unwatch = this.$watch("visibleProductIds", async productIds => {
      unwatch();
      if (productIds.length > 0) {
        const type = "impression";
        try {
          await delay(250);
          await this.trackBeacon({ type, tag: this.effectiveTag });
        } catch (error) {
          this.$bugsnag.notify(error, e => {
            e.context = `ProductCatalogueV2 VariationSearchspring mounted trackBeacon ${type}`;
          });
        }
      }
    });
  },

  methods: {
    ...mapActions("recommendations", ["trackBeacon"]),
  },
};
